import { useEffect, useState } from 'react';
import FadeIn from 'react-fade-in/lib/FadeIn';
import LoadingComponent from '../../../lib/LoadingComponent';
import { Card, Input, Button, Table, Empty, Popconfirm } from 'antd';
import ListIcon from '@mui/icons-material/List';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { getBizID, getServices } from '../../../libs/todoSlice';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { Chip } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Swal from 'sweetalert2';
import { getAvailableGroupConfigs, buildFieldColumns } from '../../../lib/preRegisterConfig';

/**
 * Generic — แทน ThaIDTable.jsx
 * เลือก config จาก group (typeKey จาก route) + services ของ business แทน hardcode 'thaid'
 * ถ้า business มีหลายประเภทใน group พร้อมกัน เลือกตัวที่ priority สูงสุด
 * (ตรงกับพฤติกรรมเดิมที่ตกลงไว้: มี general-form ด้วย -> แสดง general-form เสมอ)
 *
 * ต้องเพิ่ม field `priority` ใน preRegisterConfig.jsx ให้ thaid=1, thaidGeneralForm=2
 * (ตัวเลขสูงกว่า = ถูกเลือกก่อนเมื่อมีมากกว่า 1 ประเภทพร้อมกันใน group เดียวกัน)
 *
 * TODO: ยืนยัน field name จริงจาก response ของ list endpoint (item.other.* หรือ
 * ชื่อ field อื่น) — ตอนนี้ fallback ไปเช็ค item.other?.[key] เผื่อไว้ก่อน
 */
const PreRegisterTable = () => {
    const [loading, setLoading] = useState(false)
    const [thaidData, setThaIDData] = useState([])
    const [countMembers, setCountMembers] = useState(0)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const business_id = useSelector(getBizID)
    const services = useSelector(getServices)
    const params = useParams()   // ต้องมี :typeKey จาก route เดียวกับ PreRegisterPage.jsx
    const [searchtext, setSearchText] = useState('')
    const [defaultdata, setDefaultData] = useState([])
    const [new_user_count, setNew_User_Count] = useState(0)
    const [onlyNewData, setOnlyNewData] = useState(false)
    const [resulttotal, setResultTotal] = useState(0)

    const typeKey = params.typeKey
    const availableForms = getAvailableGroupConfigs(typeKey, services)
    const selectedConfig = [...availableForms].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0]

    const listEndpoint = selectedConfig
        ? `${process.env.REACT_APP_BACKEND_URL}/api/v6/businesses/${business_id}/${selectedConfig.listEndpointPath}/pre-registers`
        : null

    // คอลัมน์ field เสริม (นอกเหนือจาก cid/id_card_type ที่มี column เฉพาะของตัวเองอยู่แล้วด้านล่าง)
    const extraFields = selectedConfig
        ? selectedConfig.fields.filter(f => !['cid', 'id_card_type'].includes(f.key))
        : []
    const extraColumns = extraFields.length > 0
        ? buildFieldColumns({ fields: extraFields })   // read-only
        : []

    const customLocale = {
        emptyText: (<Empty description="ไม่มีข้อมูล" />)
    };

    const getThaID = async () => {
        if (!listEndpoint) return
        setLoading(true)
        try {
            const { data } = await axios({
                method: 'GET',
                url: listEndpoint,
                params: {
                    page: page,
                    perPage: pageSize,
                    search: searchtext,
                    onlyNewData: onlyNewData
                }
            })
            let prepare_data = data.data.result.filter(i => i.status === 'active').map((item, index) => {
                const extraFieldValues = Object.fromEntries(
                    extraFields.map(f => {
                        const responseField = f.responseKey ?? f.key
                        return [f.key, item[responseField] ?? item.other?.[responseField]]
                    })
                )
                return {
                    index: index + 1,
                    kind: item.kind,
                    _id: item._id,
                    createdAt: item.createdAt,
                    cid: item.idCardNum,
                    id_card_type: item.idCardType,
                    name_th: (item.accountTitleTH ? item.accountTitleTH : '') + " " +
                        (item.firstNameTH ? item.firstNameTH : '') + " " +
                        (item.lastNameTH ? item.lastNameTH : ''),
                    name_eng: (item.accountTitleENG ? item.accountTitleENG : '') + " " +
                        (item.firstNameENG ? item.firstNameENG : '') + " " +
                        (item.lastNameENG ? item.lastNameENG : ''),
                    birth_date: item.other?.birth_date,
                    ...extraFieldValues,
                    active: item.status,
                }
            })
            setCountMembers(data.data.total)
            setResultTotal(data.data.resultTotal)
            function isDateBefore24Hours(dateString) {
                const currentDate = new Date();
                const givenDate = new Date(dateString);
                const twentyFourHoursAgo = new Date();
                twentyFourHoursAgo.setHours(currentDate.getHours() - 24);
                return givenDate > twentyFourHoursAgo;
            }
            const newMembers = prepare_data.filter(item => isDateBefore24Hours(item.createdAt))
            setNew_User_Count(data.data.newData ?? newMembers.length)
            setThaIDData(prepare_data)
            setDefaultData(prepare_data)
        }
        catch (err) {
        }
        setLoading(false)
    }

    useEffect(() => {
        
        getThaID()
    }, [page, pageSize, onlyNewData, listEndpoint])

    useEffect(() => {
        setSearchText('')
        setOnlyNewData(false)
    }, [])

    const columns = [
        {
            title: 'ลำดับ',
            width: 70,
            dataIndex: 'index',
            key: 'index',
            fixed: 'left',
            responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
            render: (_, __, index) => {
                return (
                    <>{((page * pageSize) - pageSize) + index + 1}</>
                )
            }
        },
        {
            title: 'เลขประจำตัวประชาชน',
            dataIndex: 'cid',
            key: 'cid',
            width: 180,
            responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
            render: (_) => {
                if (_ === 'WAITING') {
                    return <LoadingComponent width={50} height={50} />
                }
                return _ || 'ไม่มีข้อมูล'
            }
        },
        {
            title: 'ประเภทบัตร',
            dataIndex: 'id_card_type',
            key: 'id_card_type',
            width: 130,
            responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
            render: (_) => _ || 'ไม่มีข้อมูล'
        },
        ...extraColumns,
        {
            title: 'วันที่สร้าง',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
            render: (_) => {
                if (!_) return 'ไม่มีข้อมูล'
                const date = new Date(_)
                return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
            }
        },
        {
            title: (<div>Status</div>),
            dataIndex: 'active',
            key: 'active',
            width: 160,
            responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
            render: (_) => {
                if (_ === `WAITING`) {
                    return <LoadingComponent width={50} height={50} />
                } else if (_ === 'active') {
                    return (<Chip label="pre-regis" variant='outlined' color="success" />)
                }
                return (<Chip label="revoked" variant='outlined' color="error" />)
            }
        },
        {
            title: 'การจัดการ',
            width: 100,
            fixed: 'right',
            render: (_, record) => {
                const deleteRecord = async () => {
                    try {
                        await axios({
                            method: 'DELETE',
                            url: `${process.env.REACT_APP_BACKEND_URL}/api/v5/pre-register/${record._id}?business_id=${business_id}`,
                        })
                        Swal.fire({
                            title: 'ลบข้อมูล Pre register สำเร็จแล้ว',
                            icon: 'success',
                        }).then(() => {
                            setThaIDData(thaidData.filter(e => e._id !== record._id).map((e, index) => ({ ...e, index: index + 1 })))
                            setDefaultData(defaultdata.filter(e => e._id !== record._id).map((e, index) => ({ ...e, index: index + 1 })))
                            setCountMembers(countMembers - 1)
                        })
                    } catch ({ response }) {
                    }
                }
                return (
                    <FadeIn>
                        <Popconfirm
                            title="ต้องการลบข้อมูลนี้ใช่หรือไม่?"
                            okText="ใช่"
                            cancelText="ไม่ใช่"
                            disabled={loading}
                            onConfirm={() => deleteRecord()}
                        >
                            <DeleteOutlinedIcon style={{ color: 'red' }} />
                        </Popconfirm>
                    </FadeIn>
                )
            }
        },
    ]

    return (
        <FadeIn>
            {
                loading ?
                    <LoadingComponent />
                    :
                    <>
                        <Row>
                            <Col xl={3} lg={4} md={6} sm={12} xs={12}>
                                <Card
                                    style={{
                                        width: '100%',
                                        borderRadius: '16px',
                                        marginRight: '20px',
                                        marginBottom: '10px',
                                        cursor: 'pointer',
                                        backgroundImage: onlyNewData === false ? 'linear-gradient( transparent -10%, #3776E9 150%)' : 'none',
                                    }}
                                    onClick={() => {
                                        setOnlyNewData(false)
                                        setPage(1)
                                    }}
                                >
                                    <Row>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <ListIcon style={{ backgroundColor: '#3776E9', borderRadius: '50%', fontSize: '52px', padding: '5px', color: '#fff' }} />
                                        </Col>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '31px', color: '#3776E9', fontWeight: '700' }}>{countMembers}</span>
                                            <br />
                                            สมาชิกทั้งหมด
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col xl={3} lg={4} md={6} sm={12} xs={12}>
                                <Card
                                    style={{
                                        width: '100%',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        backgroundImage: onlyNewData === true ? 'linear-gradient( transparent -10%, #053075 160%)' : 'none',
                                    }}
                                    onClick={() => {
                                        setOnlyNewData(true);
                                        setPage(1);
                                    }}
                                >
                                    <Row>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <PersonAddIcon style={{ backgroundColor: '#EFF4FD', borderRadius: '50%', fontSize: '52px', padding: '5px', color: '#053075' }} />
                                        </Col>
                                        <Col className='align-self-center' style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '31px', color: '#3D5D85', fontWeight: '700' }}>{new_user_count}</span>
                                            <br />
                                            สมาชิกใหม่
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        <div style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <div>
                                <h5>ค้นหา</h5>
                            </div>
                            <div>
                                <Input.Group compact>
                                    <Input
                                        value={searchtext}
                                        allowClear
                                        size="large"
                                        style={{ width: 'calc(50% - 40px)' }}
                                        placeholder={selectedConfig?.searchPlaceholder ?? 'ค้นหา'}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onPressEnter={() => getThaID()}
                                        maxLength={100}
                                    />
                                    <Button
                                        icon={<SearchOutlined />}
                                        size="large"
                                        style={{ borderRadius: '0px 4px 4px 0px' }}
                                        onClick={() => getThaID()}
                                    />
                                </Input.Group>
                            </div>
                        </div>
                        <Table
                            locale={customLocale}
                            columns={columns}
                            dataSource={thaidData}
                            rowKey="_id"
                            scroll={{ x: 700, y: 400 }}
                            pagination={{
                                page: page,
                                defaultCurrent: page,
                                pageSize: pageSize,
                                onChange: (page, pageSize) => {
                                    setPage(page)
                                    setPageSize(pageSize)
                                },
                                total: resulttotal
                            }}
                        />
                    </>
            }
        </FadeIn>
    )
}

export default PreRegisterTable